<script setup lang="ts" generic="NODE_DATA, NODE extends IVirtualizedTableNode<NODE_DATA>">
import type {
  IVirtualizedTableExpose,
  IVirtualizedTableNode,
} from "#/ui-kit/components/ui/virtualized-table/virtualized-table.types";

import { useElementBounding, useScroll } from "@vueuse/core";
import { computed, shallowRef, triggerRef, useTemplateRef, watchEffect } from "vue";

export interface IVirtualizedListProps<NODE_DATA, NODE extends IVirtualizedTableNode<NODE_DATA>> {
  items: NODE[];
  /**
   * Height of container in px
   */
  containerHeight?: number;
  /**
   * Height of one item in px
   */
  itemHeight?: number;
}

defineSlots<{
  cols: () => void;
  header: () => void;
  item: (props: { item: NODE }) => void;
  noResultText: () => void;
}>();

const props = withDefaults(defineProps<IVirtualizedListProps<NODE_DATA, NODE>>(), {
  containerHeight: 800,
  itemHeight: 70,
});

const tableContainer = useTemplateRef("tableContainer");
const { y: scrollTop } = useScroll(tableContainer);
const tableHeader = useTemplateRef("tableHeader");
const { height: tableHeaderHeight } = useElementBounding(tableHeader);

const items = shallowRef(props.items);

const firstMountedIndex = computed<number>(() => {
  return Math.floor(scrollTop.value / props.itemHeight);
});

const lastMountedIndex = computed<number>(() => {
  return Math.min(
    firstMountedIndex.value + Math.ceil((props.containerHeight - tableHeaderHeight.value) / props.itemHeight),
    items.value.length,
  );
});

const beforeFirstIndexInViewHeight = computed<number>(() => {
  return firstMountedIndex.value * props.itemHeight;
});

const afterLastIndexInViewHeight = computed<number>(() => {
  return (items.value.length - lastMountedIndex.value) * props.itemHeight;
});

const visibleItems = computed<NODE[]>(() => {
  return items.value.slice(firstMountedIndex.value, lastMountedIndex.value + 1);
});

function refreshView(): void {
  triggerRef(items);
}

watchEffect(() => {
  items.value = props.items;
});

defineExpose<IVirtualizedTableExpose>({
  refreshView,
});
</script>

<template>
  <div
    ref="tableContainer"
    class="w-full relative border rounded-xl bg-white overflow-auto"
    :style="{
      height: `${props.containerHeight}px`,
    }"
  >
    <table class="virtualized-table absolute inset-0">
      <slot name="cols" />

      <thead ref="tableHeader">
        <slot name="header" />
      </thead>

      <tbody>
        <tr
          v-if="beforeFirstIndexInViewHeight"
          :style="{
            height: `${beforeFirstIndexInViewHeight}px`,
          }"
        />
        <tr
          v-for="item in visibleItems"
          :key="item.id"
          :style="{
            height: `${props.itemHeight}px`,
          }"
        >
          <slot
            name="item"
            :item="item"
          />
        </tr>
        <tr
          v-if="afterLastIndexInViewHeight"
          :style="{
            height: `${afterLastIndexInViewHeight}px`,
          }"
        />
      </tbody>
    </table>

    <div
      v-if="!props.items.length"
      class="absolute inset-0 flex items-center justify-center bg-white/30"
    >
      <slot name="noResultText" />
    </div>
  </div>
</template>

<style class="scss" scoped>
.virtualized-table {
  @apply w-full rounded-xl;

  thead {
    @apply sticky top-0 bg-white z-10;
  }

  tbody {
    tr {
      box-shadow: inset 0 -1px #f0f0f4; /* border bottom */
    }
  }
}
</style>
